import { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router";

import { AutomationsRoutes } from "routes/automations/constants";

import { useMessenger } from "./use-messenger";

// Must match the query param Text app appends to the iframe's src.
const HIGHLIGHT_RULE_ID_QUERY_PARAM = "highlightRuleId";

type HighlightRuleMessage = {
  type: "rule:highlight";
  payload: { ruleId: string };
};

// Falls back to the raw value instead of throwing on a malformed percent-sequence.
const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export interface IHighlightedRule {
  /** Empty when nothing is highlighted. */
  ruleId: string;
  /** Changes per request so a repeat of the same rule still re-arms effects like scrolling. */
  requestKey: string | undefined;
}

/**
 * The rule id to highlight, read from the URL hash — normalizing a `?highlightRuleId=`
 * query param into it on mount if needed, or set directly by Text app via a
 * `rule:highlight` message when its iframe was already mounted (a cold load can't
 * be reached that way, hence the query param). Uses `window.location.hash`, not
 * react-router's cached hash: the auth bootstrap strips `#access_token` via a raw
 * `replaceState` call react-router never observes, leaving its cache stale.
 */
export const useHighlightedRule = (): IHighlightedRule => {
  const history = useHistory();
  const { key } = useLocation();
  const initialSearch = useRef(history.location.search);

  useEffect(() => {
    const params = new URLSearchParams(initialSearch.current);
    const highlightRuleId = params.get(HIGHLIGHT_RULE_ID_QUERY_PARAM);
    if (!highlightRuleId || window.location.hash) {
      return;
    }

    params.delete(HIGHLIGHT_RULE_ID_QUERY_PARAM);
    const remainingSearch = params.toString();
    history.replace(
      `${AutomationsRoutes.Rules}${remainingSearch ? `?${remainingSearch}` : ""}#${encodeURIComponent(highlightRuleId)}`
    );
  }, []);

  useMessenger<HighlightRuleMessage, never>(message => {
    if (message.type === "rule:highlight") {
      history.replace(`${AutomationsRoutes.Rules}#${encodeURIComponent(message.payload.ruleId)}`);
    }
  });

  return { ruleId: safeDecode(window.location.hash.substring(1)), requestKey: key };
};
